import { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { chatService } from '../services/chatApi';
import { DEFAULT_TIMEZONE } from '../utils/chatTime';
import {
  loadChatSession,
  saveChatSession,
  clearChatSession,
} from '../utils/chatSession';
import { useDesktopNotifications } from '../hooks/useDesktopNotifications';
import { createChatSocket } from '../services/chatSocket';
import { useMemberAuth } from './MemberAuthContext';

/**
 * Conversations state.
 *
 * Mounted once at the app root (like MusicPlayerProvider) so the panel survives
 * route changes and the unread badge stays live wherever the member is on the
 * site. The socket is only opened for authenticated members — chat has no guest
 * mode, so an anonymous visitor never connects at all.
 */

/**
 * Mirrors the server's ORDER BY in Conversation.listForMember: pinned first,
 * most recently pinned at the top of those, then everything else by recency.
 * Kept in step so an optimistic pin lands where the reload will put it.
 */
function sortConversations(list) {
  return [...list].sort((a, b) => {
    if (!!a.isPinned !== !!b.isPinned) return a.isPinned ? -1 : 1;
    if (a.isPinned && b.isPinned) {
      const byTime = new Date(b.pinnedAt || 0) - new Date(a.pinnedAt || 0);
      // Same tiebreak as the server, so an optimistic pin lands where the
      // reload will put it even when two pins share a timestamp.
      return byTime !== 0 ? byTime : b.id - a.id;
    }
    return new Date(b.lastMessageAt || 0) - new Date(a.lastMessageAt || 0);
  });
}

const ChatContext = createContext(null);

export function useChat() {
  const context = useContext(ChatContext);
  if (!context) throw new Error('useChat must be used within a ChatProvider');
  return context;
}

export function ChatProvider({ children }) {
  const { isAuthenticated, member } = useMemberAuth();

  // Read once, synchronously, so the first paint is already correct — restoring
  // in an effect would flash the panel closed and then open it.
  const [restored] = useState(loadChatSession);

  const [isPanelOpen, setIsPanelOpen] = useState(restored.isPanelOpen);
  const [conversations, setConversations] = useState([]);
  const [activeConversationId, setActiveConversationId] = useState(restored.activeConversationId);
  const [messages, setMessages] = useState([]);
  const [typingMembers, setTypingMembers] = useState([]);
  const [reactions, setReactions] = useState({});   // messageId -> [{emoji, memberId, displayName}]
  const [thread, setThread] = useState(null);       // { parent, replies }
  const [pins, setPins] = useState([]);
  const [maxPins, setMaxPins] = useState(5);
  // The zone every timestamp in the panel is rendered in. Loaded once from the
  // member's preferences so the whole app agrees on what time it is.
  const [timezone, setTimezone] = useState(DEFAULT_TIMEZONE);
  const [connection, setConnection] = useState('idle');
  /**
   * Whether Conversations is open to this member at all.
   *
   * An administrator can turn chat off site-wide (`chat_enabled`), and the
   * service also closes itself to unverified or suspended members. There is no
   * public settings endpoint carrying that flag, so rather than mirror the
   * policy here the provider asks the chat service directly — every route
   * answers 503 when it is off. Starts false so the nav icon is never shown
   * before the answer arrives.
   */
  const [chatEnabled, setChatEnabled] = useState(false);
  // Restoring into a conversation means history is still on its way. Without
  // this the panel renders "No messages yet. Say hello." for a beat before the
  // socket connects and the real history arrives.
  const [loading, setLoading] = useState(!!restored.activeConversationId);
  const [error, setError] = useState(null);

  const clientRef = useRef(null);
  const activeIdRef = useRef(null);
  const typingTimers = useRef(new Map());
  // Lets the socket handler reach openPanel, which is defined further down.
  const openPanelRef = useRef(null);
  // Lets openThreadById read the current page without depending on it.
  const messagesRef = useRef([]);
  const loadReactionsRef = useRef(null);
  const loadPinsRef = useRef(null);
  const sendMessageRef = useRef(null);
  const openConversationRef = useRef(null);
  const { notify, requestPermission, permission } = useDesktopNotifications();

  // Kept in a ref as well so socket handlers, which close over their first
  // render, always compare against the conversation that is open *now*.
  useEffect(() => {
    activeIdRef.current = activeConversationId;
  }, [activeConversationId]);

  const totalUnread = useMemo(
    () => conversations.reduce((sum, c) => sum + (c.unreadCount || 0), 0),
    [conversations]
  );

  const refreshConversations = useCallback(async () => {
    try {
      const result = await chatService.listConversations();
      setConversations(result.data || []);
      return result.data || [];
    } catch (err) {
      // Chat turned off mid-session: drop the feature rather than showing an
      // error the member can do nothing about. This runs on every reconnect, so
      // it is also how a long-lived tab notices.
      if (err.isUnavailable) setChatEnabled(false);
      else if (!err.isAuthError) setError(err.message);
      return [];
    }
  }, []);

  // ─────────────────── availability ───────────────────

  useEffect(() => {
    if (!isAuthenticated) {
      setChatEnabled(false);
      return undefined;
    }

    let cancelled = false;
    chatService
      .me()
      .then(() => { if (!cancelled) setChatEnabled(true); })
      // 503 (turned off), 401/403 (unverified, suspended) and a service that is
      // simply down all mean the same thing to the UI: do not offer it.
      .catch(() => { if (!cancelled) setChatEnabled(false); });

    return () => { cancelled = true; };
  }, [isAuthenticated]);

  // ─────────────────── socket lifecycle ───────────────────

  useEffect(() => {
    // Nothing connects until the service has confirmed it is open: the socket
    // retries forever, and pointing that at a disabled service would be a
    // reconnect loop nobody can see.
    if (!isAuthenticated || !chatEnabled) {
      clientRef.current?.close();
      clientRef.current = null;
      setConversations([]);
      setMessages([]);
      setConnection('idle');
      // Otherwise the next person to sign in on this tab would restore into the
      // previous member's conversation.
      clearChatSession();
      setIsPanelOpen(false);
      setActiveConversationId(null);
      activeIdRef.current = null;
      return undefined;
    }

    const client = createChatSocket({
      onStatus: (status) => {
        setConnection(status.state);

        // On (re)connect, pull the conversation list again and ask the server for
        // anything missed while the socket was down. The socket is a latency
        // optimization over durable history, not the delivery guarantee.
        if (status.state === 'ready') {
          refreshConversations().then((list) => {
            const openId = activeIdRef.current;
            if (openId) {
              // A restored id came from storage and was never verified, so a
              // member removed from the conversation meanwhile must not be left
              // staring at a dead panel.
              const stillAMember = list.some((c) => c.id === Number(openId));
              if (stillAMember) {
                openConversationRef.current?.(Number(openId));
              } else {
                setActiveConversationId(null);
                activeIdRef.current = null;
              }
            }
            const lastSeen = list
              .filter((c) => c.lastMessageId)
              .map((c) => ({ conversationId: c.id, lastSeenId: c.lastReadMessageId || 0 }));
            if (lastSeen.length > 0) {
              client.request('sync', { conversations: lastSeen.slice(0, 50) }).catch(() => {});
            }
          });
        }
      },
      onEvent: (event, payload) => handleSocketEvent(event, payload),
    });

    clientRef.current = client;
    return () => {
      client.close();
      clientRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, chatEnabled]);

  const handleSocketEvent = useCallback((event, payload) => {
    switch (event) {
      case 'message:new':
        if (String(payload.conversationId) === String(activeIdRef.current)) {
          if (payload.parentMessageId) {
            // Mirror it into the open thread…
            setThread((prev) =>
              prev && prev.parent.id === payload.parentMessageId
                ? { ...prev, replies: [...prev.replies, payload] }
                : prev
            );
          }

          // …and into the main list either way.
          //
          // Replies were previously routed only to the thread, but the history
          // endpoint returns them inline, so the same message appeared in one
          // place live and another after a reload. They now always show inline,
          // marked as a reply, which is what the quote in MessageList is for.
          setMessages((prev) => {
            const withParentCount = payload.parentMessageId
              ? prev.map((m) =>
                  m.id === payload.parentMessageId
                    ? { ...m, replyCount: (m.replyCount || 0) + 1 }
                    : m
                )
              : prev;

            // The sender already has an optimistic bubble under this clientMsgId.
            return withParentCount.some(
              (m) => m.id === payload.id || m.clientMsgId === payload.clientMsgId
            )
              ? withParentCount.map((m) =>
                  m.clientMsgId === payload.clientMsgId ? payload : m
                )
              : [...withParentCount, payload];
          });
        }
        break;

      case 'message:edit':
        setMessages((prev) =>
          prev.map((m) => (m.id === payload.id ? { ...m, body: payload.body, editedAt: payload.editedAt } : m))
        );
        break;

      case 'message:delete':
        setMessages((prev) =>
          prev.map((m) => (m.id === payload.id ? { ...m, body: null, deletedAt: new Date().toISOString() } : m))
        );
        break;

      case 'unread:update':
        setConversations((prev) =>
          prev.map((c) =>
            c.id === payload.conversationId ? { ...c, unreadCount: payload.unreadCount } : c
          )
        );
        break;

      case 'conversation:activity':
        setConversations((prev) => {
          const existing = prev.find((c) => c.id === payload.conversationId);
          if (!existing) {
            refreshConversations();
            return prev;
          }
          const updated = {
            ...existing,
            lastMessageId: payload.lastMessageId,
            lastMessageAt: payload.at,
          };

          // Suppressed automatically while the tab is visible — see
          // useDesktopNotifications.
          notify({
            title: existing.name || 'Conversations',
            body: `${payload.senderName}: ${payload.preview}`,
            tag: `conversation-${payload.conversationId}`,
            onClick: () => openPanelRef.current?.(payload.conversationId),
          });

          return sortConversations([
            updated,
            ...prev.filter((c) => c.id !== payload.conversationId),
          ]);
        });
        break;

      case 'conversation:new':
      case 'conversation:removed':
        refreshConversations();
        break;

      case 'reaction:add':
        setReactions((prev) => {
          const existing = prev[payload.messageId] || [];
          if (existing.some((r) => r.memberId === payload.memberId && r.emoji === payload.emoji)) {
            return prev;
          }
          return {
            ...prev,
            [payload.messageId]: [
              ...existing,
              { emoji: payload.emoji, memberId: payload.memberId, displayName: payload.displayName },
            ],
          };
        });
        break;

      case 'reaction:remove':
        setReactions((prev) => ({
          ...prev,
          [payload.messageId]: (prev[payload.messageId] || []).filter(
            (r) => !(r.memberId === payload.memberId && r.emoji === payload.emoji)
          ),
        }));
        break;

      case 'pin:add':
      case 'pin:remove':
        if (String(payload.conversationId) === String(activeIdRef.current)) {
          // Re-read rather than patching locally: the server owns ordering and
          // the limit, and a pinned message needs its author and body anyway.
          loadPinsRef.current?.(payload.conversationId);
          setMessages((prev) =>
            prev.map((m) =>
              m.id === payload.messageId ? { ...m, isPinned: event === 'pin:add' } : m
            )
          );
        }
        break;

      case 'typing:start':
        if (String(payload.conversationId) === String(activeIdRef.current)) {
          setTypingMembers((prev) =>
            prev.some((m) => m.memberId === payload.memberId) ? prev : [...prev, payload]
          );
          clearTimeout(typingTimers.current.get(payload.memberId));
          // Self-expire: a client that disconnects mid-keystroke never sends
          // typing:stop, and a stuck indicator looks broken.
          typingTimers.current.set(
            payload.memberId,
            setTimeout(() => {
              setTypingMembers((prev) => prev.filter((m) => m.memberId !== payload.memberId));
            }, 5000)
          );
        }
        break;

      case 'typing:stop':
        setTypingMembers((prev) => prev.filter((m) => m.memberId !== payload.memberId));
        break;

      default:
        break;
    }
  }, [refreshConversations, notify]);

  // ─────────────────── actions ───────────────────

  const openConversation = useCallback(async (conversationId) => {
    setActiveConversationId(conversationId);
    activeIdRef.current = conversationId;
    setMessages([]);
    setTypingMembers([]);
    setPins([]);
    setLoading(true);
    setError(null);

    try {
      await clientRef.current?.request('conversation:open', { conversationId });
      const result = await chatService.getMessages(conversationId);
      setMessages(result.data || []);

      const last = result.data?.[result.data.length - 1];
      if (last) await markRead(conversationId, last.id);

      // Lazy, and only for the page actually on screen.
      loadReactionsRef.current?.((result.data || []).map((m) => m.id));
      loadPinsRef.current?.(conversationId);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const closeConversation = useCallback(() => {
    const conversationId = activeIdRef.current;
    if (conversationId) {
      clientRef.current?.emit('conversation:close', { conversationId });
    }
    setActiveConversationId(null);
    activeIdRef.current = null;
    setMessages([]);
  }, []);

  const markRead = useCallback(async (conversationId, lastReadMessageId) => {
    setConversations((prev) =>
      prev.map((c) => (c.id === conversationId ? { ...c, unreadCount: 0, lastReadMessageId } : c))
    );
    try {
      await chatService.markRead(conversationId, lastReadMessageId);
    } catch {
      // Read state is advisory; the next open re-syncs it.
    }
  }, []);

  const sendMessage = useCallback(async (conversationId, body, { parentMessageId = null, mentions = [] } = {}) => {
    const clientMsgId = crypto.randomUUID();

    // Optimistic bubble. The server echoes back the same clientMsgId, which is
    // how this row gets replaced rather than duplicated.
    const optimistic = {
      id: `pending-${clientMsgId}`,
      clientMsgId,
      conversationId,
      body,
      parentMessageId,
      createdAt: new Date().toISOString(),
      pending: true,
      sender: { id: member?.id, displayName: 'You', photoUrl: null },
    };
    setMessages((prev) => [...prev, optimistic]);

    try {
      const saved = await clientRef.current?.request('message:send', {
        conversationId,
        clientMsgId,
        body,
        parentMessageId,
        mentions,
      });
      setMessages((prev) => prev.map((m) => (m.clientMsgId === clientMsgId ? saved : m)));
      return saved;
    } catch (err) {
      setMessages((prev) =>
        prev.map((m) => (m.clientMsgId === clientMsgId ? { ...m, failed: true, pending: false } : m))
      );
      throw err;
    }
  }, [member?.id]);

  const startConversation = useCallback(async ({ type, memberIds, name }) => {
    const result = await chatService.createConversation({ type, memberIds, name });
    await refreshConversations();
    await openConversation(result.data.id);
    return result.data;
  }, [openConversation, refreshConversations]);

  const setTyping = useCallback((conversationId, isTyping) => {
    clientRef.current?.emit(isTyping ? 'typing:start' : 'typing:stop', { conversationId });
  }, []);

  /** Reactions are loaded lazily per conversation, not with every history page. */
  const loadReactions = useCallback(async (messageIds) => {
    const results = await Promise.allSettled(
      messageIds.map((id) => chatService.getReactions(id).then((r) => [id, r.data]))
    );
    setReactions((prev) => {
      const next = { ...prev };
      for (const result of results) {
        if (result.status === 'fulfilled') {
          const [id, rows] = result.value;
          if (rows?.length) next[id] = rows;
        }
      }
      return next;
    });
  }, []);

  const toggleReaction = useCallback(async (messageId, emoji) => {
    const existing = (reactions[messageId] || []).some(
      (r) => r.memberId === member?.id && r.emoji === emoji
    );

    // Optimistic — the socket echo reconciles, and a failure re-reads.
    setReactions((prev) => {
      const rows = prev[messageId] || [];
      return {
        ...prev,
        [messageId]: existing
          ? rows.filter((r) => !(r.memberId === member?.id && r.emoji === emoji))
          : [...rows, { emoji, memberId: member?.id, displayName: 'You' }],
      };
    });

    try {
      if (existing) await chatService.removeReaction(messageId, emoji);
      else await chatService.addReaction(messageId, emoji);
    } catch (err) {
      setError(err.message);
      const fresh = await chatService.getReactions(messageId).catch(() => null);
      if (fresh) setReactions((prev) => ({ ...prev, [messageId]: fresh.data }));
    }
  }, [reactions, member?.id]);

  const openThread = useCallback(async (parentMessage) => {
    setThread({ parent: parentMessage, replies: [], loading: true });
    try {
      const result = await chatService.getThread(parentMessage.id);
      setThread({ parent: parentMessage, replies: result.data || [], loading: false });
    } catch (err) {
      setError(err.message);
      setThread({ parent: parentMessage, replies: [], loading: false });
    }
  }, []);

  const closeThread = useCallback(() => setThread(null), []);

  /**
   * Open a thread when all that is known is the parent's id — which is the case
   * for the "replying to…" quote on an inline reply.
   *
   * The parent is usually already on screen, so this resolves it from the loaded
   * page first and only falls back to a fetch when the reply has scrolled far
   * enough from its parent that the parent is no longer loaded.
   */
  const openThreadById = useCallback(async (parentMessageId) => {
    const loaded = messagesRef.current.find((m) => m.id === parentMessageId);
    if (loaded) return openThread(loaded);

    try {
      const result = await chatService.getThread(parentMessageId);
      const replies = result.data || [];
      // The thread endpoint returns replies, not the parent, so synthesise
      // enough of it to render the header rather than showing a blank one.
      const parent = replies[0]?.replyTo
        ? {
            id: parentMessageId,
            conversationId: replies[0].conversationId,
            body: replies[0].replyTo.preview,
            createdAt: null,
            sender: {
              id: replies[0].replyTo.senderMemberId,
              displayName: replies[0].replyTo.senderName,
            },
          }
        : { id: parentMessageId, body: null, sender: { displayName: 'Unknown member' } };

      setThread({ parent, replies, loading: false });
      return undefined;
    } catch (err) {
      setError(err.message);
      return undefined;
    }
  }, [openThread]);

  /** Send a message carrying files. REST-only — the socket cannot carry binary. */
  const sendWithAttachments = useCallback(async (conversationId, files, body = '') => {
    const clientMsgId = crypto.randomUUID();

    const optimistic = {
      id: `pending-${clientMsgId}`,
      clientMsgId,
      conversationId,
      body: body || null,
      hasAttachments: true,
      uploading: true,
      progress: 0,
      createdAt: new Date().toISOString(),
      pending: true,
      sender: { id: member?.id, displayName: 'You', photoUrl: null },
      attachments: files.map((file, index) => ({
        id: `pending-${index}`,
        filename: file.name,
        mime: file.type,
        bytes: file.size,
        inline: file.type.startsWith('image/'),
        pending: true,
      })),
    };
    setMessages((prev) => [...prev, optimistic]);

    try {
      const result = await chatService.uploadAttachments(conversationId, {
        clientMsgId,
        body,
        files,
        onProgress: (progress) => {
          setMessages((prev) =>
            prev.map((m) => (m.clientMsgId === clientMsgId ? { ...m, progress } : m))
          );
        },
      });
      setMessages((prev) => prev.map((m) => (m.clientMsgId === clientMsgId ? result.data : m)));
      return result.data;
    } catch (err) {
      setMessages((prev) =>
        prev.map((m) =>
          m.clientMsgId === clientMsgId
            ? { ...m, failed: true, pending: false, uploading: false, error: err.message }
            : m
        )
      );
      throw err;
    }
  }, [member?.id]);

  const sendThreadReply = useCallback(async (body) => {
    if (!thread?.parent) return null;
    return sendMessageRef.current?.(thread.parent.conversationId, body, {
      parentMessageId: thread.parent.id,
    });
  }, [thread?.parent]);

  const loadPins = useCallback(async (conversationId) => {
    try {
      const result = await chatService.getPins(conversationId);
      setPins(result.data.pins || []);
      setMaxPins(result.data.max ?? 5);
    } catch {
      // Pins are an enhancement; a failure here must not break the conversation.
    }
  }, []);

  const togglePin = useCallback(async (message) => {
    try {
      const result = message.isPinned
        ? await chatService.unpinMessage(message.id)
        : await chatService.pinMessage(message.id);

      setPins(result.data.pins || []);
      if (result.data.max !== undefined) setMaxPins(result.data.max);
      setMessages((prev) =>
        prev.map((m) => (m.id === message.id ? { ...m, isPinned: !message.isPinned } : m))
      );
      return { ok: true };
    } catch (err) {
      // 409 is the limit being reached — a normal outcome the UI should explain,
      // not an error to swallow.
      return { ok: false, message: err.message };
    }
  }, []);

  /**
   * Pin a conversation to the top of this member's own list.
   *
   * Per member — pinning the shepherd group does not reorder it for the other
   * sixteen people in it.
   */
  const toggleConversationPin = useCallback(async (conversation) => {
    const pin = !conversation.isPinned;

    // Optimistic, including the re-sort, so the row moves under the cursor.
    setConversations((prev) => {
      const next = prev.map((c) =>
        c.id === conversation.id
          ? { ...c, isPinned: pin, pinnedAt: pin ? new Date().toISOString() : null }
          : c
      );
      return sortConversations(next);
    });

    try {
      if (pin) await chatService.pinConversation(conversation.id);
      else await chatService.unpinConversation(conversation.id);
      return { ok: true };
    } catch (err) {
      // Hitting the limit is a normal outcome; put the list back and say why.
      await refreshConversations();
      return { ok: false, message: err.message };
    }
  }, [refreshConversations]);

  const searchMessages = useCallback(
    (queryText, conversationId = null) => chatService.search(queryText, conversationId),
    []
  );

  const openPanel = useCallback((conversationId = null) => {
    setIsPanelOpen(true);
    // Asked here rather than on page load: this is a genuine user gesture, and
    // prompting cold is how people end up clicking Block permanently.
    requestPermission();
    if (conversationId) openConversation(conversationId);
  }, [openConversation, requestPermission]);

  const closePanel = useCallback(() => {
    setIsPanelOpen(false);
    setThread(null);
    closeConversation();
  }, [closeConversation]);

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    openPanelRef.current = openPanel;
  }, [openPanel]);

  useEffect(() => {
    loadReactionsRef.current = loadReactions;
  }, [loadReactions]);

  useEffect(() => {
    loadPinsRef.current = loadPins;
  }, [loadPins]);

  // Mirror the panel's visible state so a reload or a hard navigation comes back
  // to the same place.
  useEffect(() => {
    if (!isAuthenticated) return;
    saveChatSession({ isPanelOpen, activeConversationId });
  }, [isAuthenticated, isPanelOpen, activeConversationId]);

  useEffect(() => {
    sendMessageRef.current = sendMessage;
  }, [sendMessage]);

  useEffect(() => {
    openConversationRef.current = openConversation;
  }, [openConversation]);

  // Load the list once on login so the badge is correct before the panel is
  // ever opened. Waits for the availability answer so a disabled service is
  // never called.
  useEffect(() => {
    if (isAuthenticated && chatEnabled) refreshConversations();
  }, [isAuthenticated, chatEnabled, refreshConversations]);

  useEffect(() => {
    if (!isAuthenticated || !chatEnabled) return;
    chatService
      .getNotificationPrefs()
      .then((r) => setTimezone(r.data?.timezone || DEFAULT_TIMEZONE))
      .catch(() => setTimezone(DEFAULT_TIMEZONE));
  }, [isAuthenticated, chatEnabled]);

  const value = {
    chatEnabled,
    isPanelOpen,
    openPanel,
    closePanel,
    conversations,
    activeConversationId,
    messages,
    typingMembers,
    reactions,
    thread,
    pins,
    maxPins,
    timezone,
    totalUnread,
    connection,
    loading,
    error,
    notificationPermission: permission,
    openConversation,
    closeConversation,
    sendMessage,
    startConversation,
    markRead,
    setTyping,
    refreshConversations,
    toggleReaction,
    togglePin,
    toggleConversationPin,
    openThread,
    openThreadById,
    closeThread,
    sendThreadReply,
    sendWithAttachments,
    searchMessages,
    requestNotificationPermission: requestPermission,
  };

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>;
}

export default ChatContext;
